import { Pipe, PipeTransform, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { AppFacadeService } from '../services/app-facade.service';
import { DepotTimezoneFormatterService } from '../services/depot-timezone-formatter.service';
import { OrganizationType } from '../models/organization-type';
import { DepotTimezoneDateFormat } from '../enums/depot-timezone-date-format.enum';

/**
 * Pipe to format dates using depot timezone if available, otherwise browser timezone.
 * Respects organization locale for date/time format (en-GB for UK, en-US for US).
 *
 * This pipe is a THIN WRAPPER around DepotTimezoneFormatterService.
 * All business logic is in the service (single source of truth).
 * The pipe just provides Observable-based reactivity for Angular templates.
 *
 * Usage:
 *   {{ contract.CreatedDate | depotTimezoneDate }}                          // Uses default depot (001), org locale
 *   {{ contract.PickupDate | depotTimezoneDate:'short' }}                   // Uses default depot (001) with short format
 *   {{ contract.CreatedDate | depotTimezoneDate:'full':contract.DepotId }}  // Uses contract's depot timezone
 *   {{ invoice.Date | depotTimezoneDate:'dateOnly':'003' }}                 // Uses depot '003' timezone
 *
 * Architecture:
 *   - Uses AppFacadeService to access depots and locale from config (reactive)
 *   - Delegates all formatting logic to DepotTimezoneFormatterService (DRY)
 *   - Returns Observable for reactive updates when config changes
 *
 * Timezone Resolution (Expert RMS only):
 *   1. If depotId parameter is provided, use that depot's timezone from config
 *   2. Otherwise, default to depot '001' timezone
 *   3. Fall back to browser timezone if depot not found or invalid timezone
 *
 * Locale Resolution:
 *   - Uses organization config.Locale (e.g., 'en-GB' for UK, 'en-US' for US)
 *   - en-GB: DD/MM/YYYY format, 24-hour clock
 *   - en-US: MM/DD/YYYY format, 12-hour clock with AM/PM
 *   - Defaults to 'en-US' if not specified
 *
 * Parameters:
 *   - value: Date/time to format (Date, ISO string, or timestamp)
 *   - format: Display format ('full', 'short', 'dateOnly', 'timeOnly')
 *   - depotId: Optional depot ID (e.g., '001', '003'). Defaults to '001' if not provided.
 *
 * Formats (examples for en-US / en-GB):
 *   'full' (default): Jun 15, 2025, 10:30 AM EST / 15 Jun 2025, 22:30 BST (with timezone)
 *   'short': 6/15/25, 10:30 AM / 15/6/25, 22:30
 *   'dateOnly': 6/15/2025 / 15/6/2025
 *   'timeOnly': 10:30 AM EST / 22:30 BST (with timezone)
 *
 * Organization Type Behavior:
 *   - Expert: Uses depot timezone (passed depotId or defaults to '001') with timezone labels
 *   - Syrinx: Uses UTC (NO timezone conversion - shows times exactly as API returns) WITHOUT timezone labels (respects locale for format)
 *   - Essential: Uses browser timezone with timezone labels
 */
@Pipe({
    standalone: false,
    name: 'depotTimezoneDate',
    pure: true // Pure pipe - only re-evaluates when input changes
})
export class DepotTimezoneDatePipe implements PipeTransform {
    private readonly appFacade = inject(AppFacadeService);
    private readonly formatter = inject(DepotTimezoneFormatterService);

    transform(
        value: Date | string | number | null | undefined,
        format: DepotTimezoneDateFormat | 'full' | 'short' | 'dateOnly' | 'timeOnly' = DepotTimezoneDateFormat.Full,
        depotId?: string
    ): Observable<string> {
        if (!value) {
            return new Observable(observer => {
                observer.next('');
                observer.complete();
            });
        }

        // Get config reactively, then call service to format
        return this.appFacade.getConfig().pipe(
            map(config => {
                // Convert string literal to enum value for type safety
                const formatEnum = this.toEnum(format);

                // Delegate ALL formatting logic to the service (including locale for proper date/time formatting)
                return this.formatter.format(value, formatEnum, config?.organizationType as OrganizationType | undefined, config?.depots, depotId, config?.Locale);
            })
        );
    }

    /**
     * Converts string literal to enum value.
     * Allows templates to use string literals while service uses type-safe enums.
     */
    private toEnum(format: DepotTimezoneDateFormat | 'full' | 'short' | 'dateOnly' | 'timeOnly'): DepotTimezoneDateFormat {
        if (typeof format === 'string') {
            // Map string literals to enum values
            switch (format) {
                case 'short':
                    return DepotTimezoneDateFormat.Short;
                case 'dateOnly':
                    return DepotTimezoneDateFormat.DateOnly;
                case 'timeOnly':
                    return DepotTimezoneDateFormat.TimeOnly;
                case 'full':
                default:
                    return DepotTimezoneDateFormat.Full;
            }
        }
        return format; // Already an enum value
    }
}
